export const PAGE_CONTEXT: Record<string, {
    name: string;
    purpose: string;
    nextSteps: string[];
    relatedPages: Array<{ path: string; label: string; why: string }>;
    category: string;
}> = {
    '/dashboard': {
        name: 'Dashboard',
        category: 'Core',
        purpose: 'Overview of your project health, RPN trends, and quick access to FMEAs',
        nextSteps: ['Create new FMEAs', 'Review high-RPN items', 'Check materials database', 'View knowledge graph'],
        relatedPages: [
            { path: '/fmeas', label: 'FMEA Management', why: 'See all your failure modes' },
            { path: '/graph-view', label: 'Knowledge Graph', why: 'Visualize all relationships' },
            { path: '/materials-database', label: 'Materials', why: 'Check component specs' }
        ]
    },
    '/fmeas': {
        name: 'FMEA Management',
        category: 'Risk Analysis',
        purpose: 'View and manage all your Failure Modes and Effects Analysis',
        nextSteps: ['Create new FMEA', 'Edit existing FMEA', 'Export FMEAs', 'View FMEA in graph'],
        relatedPages: [
            { path: '/fmea-editor', label: 'FMEA Editor', why: 'Create or edit individual FMEAs' },
            { path: '/graph-view', label: 'Knowledge Graph', why: 'See FMEA relationships' },
            { path: '/dvpr', label: 'DVP&R', why: 'Link tests to FMEAs' },
            { path: '/risk-analysis', label: 'Risk Analysis', why: 'Deep dive into RPN scores' }
        ]
    },
    '/fmea-editor': {
        name: 'FMEA Editor',
        category: 'Risk Analysis',
        purpose: 'Create and edit individual Failure Modes with severity, occurrence, and detection scores',
        nextSteps: ['Add failure mode', 'Calculate RPN', 'Add controls', 'Link to DVP&R tests'],
        relatedPages: [
            { path: '/fmeas', label: 'FMEA Management', why: 'See all FMEAs' },
            { path: '/risk-analysis', label: 'Risk Analysis', why: 'Analyze RPN trends' },
            { path: '/dvpr', label: 'DVP&R', why: 'Create detection tests' }
        ]
    },
    '/risk-analysis': {
        name: 'Risk Analysis',
        category: 'Risk Analysis',
        purpose: 'Deep dive into RPN trends, risk matrices, and mitigation tracking',
        nextSteps: ['Review high RPNs', 'Check S×O vs S×D matrix', 'Track mitigation progress'],
        relatedPages: [
            { path: '/fmeas', label: 'FMEA Management', why: 'View individual FMEAs' },
            { path: '/fmea-editor', label: 'FMEA Editor', why: 'Edit high-risk items' }
        ]
    },
    '/p-diagram': {
        name: 'P-Diagram',
        category: 'System Design',
        purpose: 'View Parameter Diagrams showing system context, noise factors, and ideal functions',
        nextSteps: ['Create P-Diagram', 'Link to FMEAs', 'Identify noise factors'],
        relatedPages: [
            { path: '/p-diagram-generator', label: 'P-Diagram Generator', why: 'AI-powered diagram creation' },
            { path: '/fmeas', label: 'FMEA Management', why: 'Link failure modes to noise factors' }
        ]
    },
    '/p-diagram-generator': {
        name: 'P-Diagram Generator',
        category: 'System Design',
        purpose: 'AI-powered tool to generate Parameter Diagrams from system descriptions',
        nextSteps: ['Describe system', 'Generate diagram', 'Review and refine', 'Save to P-Diagram page'],
        relatedPages: [
            { path: '/p-diagram', label: 'P-Diagram', why: 'View generated diagrams' }
        ]
    },
    '/pbs-manager': {
        name: 'PBS Manager',
        category: 'Product Structure',
        purpose: 'Manage Product Breakdown Structure showing component hierarchy',
        nextSteps: ['Add components', 'Define hierarchy', 'Link to FMEAs'],
        relatedPages: [
            { path: '/bom-functions', label: 'BOM Functions', why: 'Define component functions' },
            { path: '/fmeas', label: 'FMEA Management', why: 'Link components to failure modes' }
        ]
    },
    '/bom-functions': {
        name: 'BOM Functions',
        category: 'Product Structure',
        purpose: 'Define the basic functions for each Bill of Materials component',
        nextSteps: ['Define functions', 'Link to requirements', 'Create FMEAs for functions'],
        relatedPages: [
            { path: '/pbs-manager', label: 'PBS Manager', why: 'See component hierarchy' },
            { path: '/fmeas', label: 'FMEA Management', why: 'Create failure modes for functions' }
        ]
    },
    '/dvpr': {
        name: 'DVP&R',
        category: 'Testing & Validation',
        purpose: 'Design Verification Plan & Report - test specifications linked to FMEAs',
        nextSteps: ['Create test specs', 'Link to FMEAs', 'Track test results', 'Update detection scores'],
        relatedPages: [
            { path: '/fmeas', label: 'FMEA Management', why: 'Link tests to failure modes' },
            { path: '/materials-database', label: 'Materials Database', why: 'Specify material tests' }
        ]
    },
    '/materials-database': {
        name: 'Materials Database',
        category: 'Product Data',
        purpose: 'Material specifications, validation data, and supplier information',
        nextSteps: ['Add materials', 'Validate specs', 'Link to components', 'Track test results'],
        relatedPages: [
            { path: '/bom-functions', label: 'BOM Functions', why: 'Link materials to components' },
            { path: '/dvpr', label: 'DVP&R', why: 'Create material validation tests' }
        ]
    },
    '/graph-view': {
        name: 'Knowledge Graph',
        category: 'Visualization',
        purpose: 'Interactive graph showing relationships between functions, FMEAs, controls, and tests',
        nextSteps: ['Explore connections', 'Find weak links', 'Identify missing links'],
        relatedPages: [
            { path: '/fmeas', label: 'FMEA Management', why: 'View FMEAs in detail' },
            { path: '/traceability-matrix', label: 'Traceability Matrix', why: 'See complete traceability' }
        ]
    },
    '/traceability-matrix': {
        name: 'Traceability Matrix',
        category: 'Documentation',
        purpose: 'Complete traceability from requirements through FMEAs to tests',
        nextSteps: ['Check completeness', 'Find gaps', 'Export for audits'],
        relatedPages: [
            { path: '/graph-view', label: 'Knowledge Graph', why: 'Visual representation' },
            { path: '/customer-requirements', label: 'Customer Requirements', why: 'Source requirements' }
        ]
    },
    '/customer-requirements': {
        name: 'Customer Requirements',
        category: 'Planning',
        purpose: 'Track customer requirements and link them to product features and tests',
        nextSteps: ['Add requirements', 'Link to features', 'Create FMEAs', 'Set up validation'],
        relatedPages: [
            { path: '/traceability-matrix', label: 'Traceability Matrix', why: 'Verify requirement coverage' },
            { path: '/fmeas', label: 'FMEA Management', why: 'Identify risks to requirements' }
        ]
    },
    '/ecn-management': {
        name: 'ECN Management',
        category: 'Change Management',
        purpose: 'Engineering Change Notices tracking design iterations and RPN improvements',
        nextSteps: ['Create ECN', 'Track RPN changes', 'Document iterations', 'Get approvals'],
        relatedPages: [
            { path: '/fmeas', label: 'FMEA Management', why: 'See RPN improvements from ECNs' },
            { path: '/risk-analysis', label: 'Risk Analysis', why: 'Analyze impact of changes' }
        ]
    },
    '/ai-assistant': {
        name: 'AI Assistant',
        category: 'AI Tools',
        purpose: 'AI-powered analysis and reporting on reliability data',
        nextSteps: ['Select prompt', 'Generate report', 'Review insights', 'Export results'],
        relatedPages: [
            { path: '/fmeas', label: 'FMEA Management', why: 'Analyze FMEA data' },
            { path: '/risk-analysis', label: 'Risk Analysis', why: 'Get AI insights on risks' }
        ]
    }
};

export const MAGNUS_KNOWLEDGE = {
    greetings: [
        "Hey! I'm Magnus. I can help you find pages or answer FMEA questions. What are you looking for?",
        "Hi! Need help with the app or detailed reliability analysis? Toggle Prime for expert help!",
        "Welcome! Looking for a specific page? Just ask and I'll tell you exactly where it is in the menu.",
    ],

    // FMEA Sheet specific actions
    fmeaSheet: {
        addTest: {
            answer: "To add a test, you need to create a DVP&R test plan linked to your failure mode. \n\nGo to the DVP&R page (Sidebar → Testing & Validation) to create test specifications.",
            action: { type: 'navigate' as const, target: '/dvpr', label: 'Go to DVP&R' }
        },
        addRow: {
            answer: "To add a new row (Failure Mode), go to the FMEA Editor page. Click the '+ Add Failure Mode' button or select an existing function to add more failure modes to it.",
            action: { type: 'navigate' as const, target: '/fmea-editor', label: 'Go to FMEA Editor' }
        },
        deleteRow: {
            answer: "You can delete rows in the FMEA Editor. Select the failure mode and look for the delete (trash can) icon. Be careful, this can't be undone!",
            action: { type: 'navigate' as const, target: '/fmea-editor', label: 'Go to FMEA Editor' }
        },
        filter: {
            answer: "You can filter FMEAs in the FMEA Management page. Use the filter bar at the top to search by keywords, severity levels, or RPN scores.",
            action: { type: 'navigate' as const, target: '/fmeas', label: 'Go to FMEA Management' }
        }
    },

    // Conversational FMEA & Reliability Knowledge with specific directions
    fmeaHelp: {
        rpn: {
            answer: "RPN is just Severity times Occurrence times Detection. It tells you which risks to tackle first. Over 100 means you should do something about it. Safety stuff should be under 50.\n\nFind the FMEA Editor in the left sidebar - it's in the Risk Analysis section.",
            action: { type: 'navigate' as const, target: '/fmea-editor', label: 'Go to FMEA Editor' }
        },
        severity: {
            answer: "Severity is how bad the failure is (1-10). Think: 9-10 is catastrophic (safety), 7-8 is serious (product breaks), 5-6 is annoying, 1-4 is barely noticeable.\n\nYou can only change severity by redesigning. Check your FMEAs in the sidebar under Risk Analysis → FMEA Management.",
            action: { type: 'navigate' as const, target: '/fmeas', label: 'Go to FMEA Management' }
        },
        occurrence: {
            answer: "To reduce occurrence, make the failure less likely: improve design, use better materials, add redundancy, or tighten controls.\n\nSee how occurrence scores dropped over iterations in the sidebar: Change Management → ECN Management.",
            action: { type: 'navigate' as const, target: '/ecn-management', label: 'Go to ECN Management' }
        },
        detection: {
            answer: "Detection is about catching failures before customers see them. Add tests, inspections, or sensors. Lower detection score = easier to catch.\n\nCreate verification tests in the sidebar: Testing & Validation → DVP&R.",
            action: { type: 'navigate' as const, target: '/dvpr', label: 'Go to DVP&R' }
        },
        controls: {
            answer: "Two control types: Prevention (stops failures, reduces occurrence) and Detection (catches them, reduces detection score).\n\nFor serious risks, you want both. See them in FMEA Management (sidebar → Risk Analysis section).",
            action: { type: 'navigate' as const, target: '/fmeas', label: 'Go to FMEA Management' }
        },
        mitigation: {
            answer: "Game plan: Tackle RPN over 100 first. Pay special attention to safety items (severity 8+). Try reducing severity, then occurrence, then add detection.\n\nTrack progress in sidebar → Risk Analysis → Risk Analysis page.",
            action: { type: 'navigate' as const, target: '/risk-analysis', label: 'Go to Risk Analysis' }
        }
    },

    reliabilityMethods: {
        pDiagram: {
            answer: "P-Diagrams show what your system does, what goes in/out, what can mess it up (noise factors), and how you control it.\n\nFind them in the left sidebar: System Design → P-Diagram. Use P-Diagram Generator to create new ones.",
            action: { type: 'navigate' as const, target: '/p-diagram', label: 'Go to P-Diagram' }
        },
        reliability: {
            answer: "MTTF is mean time to failure. MTBF is for repairable things. These connect to your occurrence scores in FMEAs.\n\nB10 life (when 10% fail) is useful for warranty planning.",
        },
        weakLink: {
            answer: "Usually 20% of components cause 80% of problems. Find those weak links and focus there.\n\nThe Knowledge Graph shows which components have the most FMEAs - it's in the sidebar under Visualization section.",
            action: { type: 'navigate' as const, target: '/graph-view', label: 'Go to Knowledge Graph' }
        },
        riskMatrix: {
            answer: "Risk matrix plots S×O vs S×D. Shows what's likely to happen vs what's hard to catch. Worst corner: high on both (likely failures you won't catch).\n\nSee this in the sidebar: Risk Analysis → Risk Analysis page.",
            action: { type: 'navigate' as const, target: '/risk-analysis', label: 'Go to Risk Analysis' }
        },
        traceability: {
            answer: "Traceability shows everything connects: requirements → functions → FMEAs → controls → tests. Crucial for audits.\n\nFind the Traceability Matrix in the sidebar under Documentation section.",
            action: { type: 'navigate' as const, target: '/traceability-matrix', label: 'Go to Traceability Matrix' }
        }
    },

    // Navigation help - very specific about sidebar
    navigation: {
        'fmea-editor': {
            answer: "**FMEA Editor is in the left sidebar.**\n\nLook for the Risk Analysis section. Click to expand it if needed, then click 'FMEA Editor'.\n\nThis is where you create and edit individual failure modes.",
            action: { type: 'navigate' as const, target: '/fmea-editor', label: 'Go to FMEA Editor' }
        },
        'fmeas': {
            answer: "**FMEA Management is in the left sidebar.**\n\nLook in the Risk Analysis section → 'FMEA Management'. This shows all your failure modes in one place.",
            action: { type: 'navigate' as const, target: '/fmeas', label: 'Go to FMEA Management' }
        },
        'p-diagram': {
            answer: "**P-Diagram is in the left sidebar.**\n\nLook in the System Design section → 'P-Diagram'. You can also use 'P-Diagram Generator' to create new ones with AI.",
            action: { type: 'navigate' as const, target: '/p-diagram', label: 'Go to P-Diagram' }
        },
        'pbs': {
            answer: "**PBS Manager is in the left sidebar.**\n\nLook in the Product Structure section → 'PBS Manager'. This is your Product Breakdown Structure.",
            action: { type: 'navigate' as const, target: '/pbs-manager', label: 'Go to PBS Manager' }
        },
        'dvpr': {
            answer: "**DVP&R is in the left sidebar.**\n\nLook in the Testing & Validation section → 'DVP&R'. This is where you manage test specs and results.",
            action: { type: 'navigate' as const, target: '/dvpr', label: 'Go to DVP&R' }
        },
        'materials': {
            answer: "**Materials Database is in the left sidebar.**\n\nLook in the Product Data section → 'Materials Database'. This has all your material specs and validation data.",
            action: { type: 'navigate' as const, target: '/materials-database', label: 'Go to Materials Database' }
        },
        'graph': {
            answer: "**Knowledge Graph is in the left sidebar.**\n\nLook in the Visualization section → 'Knowledge Graph'. Click nodes to explore relationships between functions, FMEAs, and tests.",
            action: { type: 'navigate' as const, target: '/graph-view', label: 'Go to Knowledge Graph' }
        }
    },

    // General FAQ
    faq: {
        'help': {
            answer: "I can help you:\n• Find pages in the sidebar (just ask \"where is [page name]\")\n• Explain FMEA stuff (RPN, severity, occurrence, detection)\n• Guide you through reliability methods\n• Analyze your FMEA data and find problems\n\nWhat do you need?",
        }
    },

    fallback: "I'm not sure what you're looking for. Try asking:\n• \"Where is the FMEA Editor?\"\n• \"How do I get to DVP&R?\"\n• \"Show me P-Diagrams\"\n• \"What is RPN?\"\n• \"Do you see any problems on this page?\""
};
